import { useState, useEffect, useCallback } from 'react';
import { Theme, Typography, List, ListItem, ListItemIcon, ListItemText } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { useAppState } from '../../../state';
import useVideoContext from '../../../hooks/useVideoContext/useVideoContext';
import { capitalize, createRoomName } from '../../../utils';
import { apiGetActiveMeetRooms, apiGetRoomConfig } from '../../../utils/api';
import { IActiveRoom } from '../../../types';

const useStyles = makeStyles((theme: Theme) => ({
  roomsContainer: {
    background: '#fff',
    borderLeft: 'solid 1px #e2e2e2',
    padding: '1em',
    width: '280px',
    position: 'relative',
    overflow: 'auto',
    [theme.breakpoints.down('md')]: {
      width: '100%',
    },
  },
  listContainer: {
    marginTop: '8px',
  },
  noActiveRoomMsg: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    [theme.breakpoints.down('sm')]: {
      top: 'auto',
      left: 'auto',
      position: 'relative',
      transform: 'none',
    },
  },
  listItem: {
    position: 'relative',
    paddingLeft: '2em',
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: '#ebebeb',
    },
  },
  listItemRoot: {
    minWidth: 'auto',
  },
  onlineIcon: {
    position: 'absolute',
    left: '.5em',
    top: '50%',
    transform: 'translateY(-50%)',
    height: '10px',
    width: '10px',
    borderRadius: '50%',
    background: 'green',
  },
}));

const ActiveRooms = () => {
  const classes = useStyles();
  const { username, domain } = useAppState();
  const [activeRooms, setActiveRooms] = useState<IActiveRoom[]>([]);
  const { joinOtherUserRoom } = useVideoContext();
  //const URL = 'wss://websockettest.avataracloud.com';

  /*function sendConnectKnock() {
    var ws = new WebSocket(URL);
    var msg = {
      type: 'knock',
      knockerUsername: username,
      domainUserId: 20943,
      id: '37bd3153445b4939b83f1fadb0d93c7b',
    };
    ws.onopen = function() {
      ws.send(JSON.stringify(msg));
      console.log('Connected');
    };
  }*/

  // grab all active rooms and sets them with the appropriate data
  const fetchActiveRooms = useCallback(async () => {
    let data = await apiGetActiveMeetRooms(username, domain);
    data = data.filter(room => room.username.toLowerCase() !== username.toLowerCase());
    setActiveRooms(data);
  }, [username, domain]);

  useEffect(() => {
    // grabs all currently active rooms if it sees a username and domain
    let timer: number | null;
    if (username && domain) {
      fetchActiveRooms();
      // grabs active rooms every 15 seconds while username and domains are there
      timer = window.setInterval(fetchActiveRooms, 15000);
    }
    // clears interval after all active rooms are obtained
    return () => {
      timer && window.clearInterval(timer);
    };
  }, [username, domain, fetchActiveRooms]);

  const handleRoomClick = (room: IActiveRoom) => {
    return async () => {
      // room is private
      const roomName = createRoomName(room.username, domain);
      const roomConfig = await apiGetRoomConfig(roomName);
      await joinOtherUserRoom(roomConfig, roomName, username);
    };
  };

  return (
    <div className={classes.roomsContainer}>
      <Typography variant="h6">Active Rooms</Typography>
      {!!activeRooms.length && (
        <List className={classes.listContainer} component="ul">
          {activeRooms.map(room => (
            <ListItem className={classes.listItem} key={room.username} onClick={handleRoomClick(room)} divider>
              <ListItemIcon classes={{ root: classes.listItemRoot }}>
                <div className={classes.onlineIcon}></div>
              </ListItemIcon>
              <ListItemText>
                {capitalize(room.username)} ({room.roommembers.split(',').length})
              </ListItemText>
            </ListItem>
          ))}
        </List>
      )}
      {!activeRooms.length && (
        <Typography className={classes.noActiveRoomMsg} variant="body1">
          No active rooms!
        </Typography>
      )}
    </div>
  );
};

export default ActiveRooms;
